<template>
  <div 
    class="knowledge-list"
    :class="{ 'knowledge-list--empty': hasNoContent }"
    style="height:100%;overflow:auto;position:relative"
    @drop="handleFileDrop"
    @dragover.prevent
    @dragenter.prevent
  >
    <BaseEmpty v-if="hasNoContent" class="kng-empty-fill">
      <img :src="emptyContentImg" class="empty-content-animated kng-empty-img" alt="" />
      <template v-if="!dirSelected">
        <span class="l-empty-message">请选择左侧目录</span>
        <span class="kng-empty-hint">选择目录后即可浏览和管理文件</span>
      </template>
      <template v-else>
        <span class="l-empty-message">暂无内容</span>
        <label v-if="dirEditable && canUpload" class="kng-empty-upload-btn">
          <input type="file" multiple :accept="uploadAccept || undefined" style="display:none" @change="handleEmptyUpload" />
          <BaseIcon icon="ios-cloud-upload-outline" :size="16" />
          {{ emptyUploadLabel }}
        </label>
      </template>
    </BaseEmpty>
    
    <!-- 图标视图 -->
    <div v-else-if="viewMode === 'icon'" class="icon-view" @click="handleContainerClick">
      <!-- 子目录 -->
      <div
        v-for="sub in subdirs"
        :key="getSubdirKey(sub)"
        class="icon-item icon-item-dir"
        :class="{ 'drop-target': dirEditable }"
        v-droppable="dirEditable ? handleDropInSubdir : null"
        :data-dir-id="sub.id"
        @click.stop="$emit('on-dir-select', sub)"
        @contextmenu.prevent="showItemContextMenu($event, null, sub)"
      >
        <div class="icon-item-content">
          <div class="icon-item-icon-wrapper">
            <BaseIcon :icon="getSubdirIconMeta(sub).icon" :size="48" :color="getSubdirIconMeta(sub).color" />
            <BaseIcon v-if="getSubdirIconMeta(sub).refBadge" icon="lianjie" :size="14" class="ref-badge-icon" />
          </div>
          <div class="icon-item-name">{{ sub.name }}</div>
        </div>
      </div>
      <div
        v-for="item in items"
        :key="item.id"
        class="icon-item"
        :class="{ 'selected': isSelected(item.id) }"
        v-draggable="dirEditable ? item.id : null"
        v-droppable="handleDropInFolder"
        :data-item-id="item.id"
        @click.stop="handleItemClick(item)"
        @dblclick="handleItemDoubleClick(item)"
        @contextmenu.prevent="showItemContextMenu($event, item, null)"
      >
        <span class="kng-type-badge" :class="getTypeBadgeClass(item)">{{ getTypeLabel(item) }}</span>
        <div class="icon-item-content">
          <div class="icon-item-icon-wrapper">
            <BaseIcon :icon="getItemIcon(item)" :size="44" :color="getIconColor(item)" />
            <BaseIcon v-if="item.ref" icon="lianjie" :size="14" class="ref-badge-icon" />
          </div>
          <div class="icon-item-name" :title="getDisplayTitle(item)">{{ getDisplayTitle(item) }}</div>
          <div class="icon-item-status">
            <span v-if="isAuditPending(item)" class="icon-status-tag pending">未审核</span>
            <span v-if="getRagLabel(item)" class="icon-status-tag" :class="getRagClass(item)">{{ getRagLabel(item) }}</span>
            <span v-if="showMarkdownBtn(item)" class="icon-status-tag md-parse" :class="{ done: item.has_markdown || item.markdown_content }">已解析</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 列表视图（表格） -->
    <div v-else-if="viewMode === 'list'" class="list-view">
      <table class="kng-table">
        <thead>
          <tr>
            <th class="kng-col-check"></th>
            <th class="kng-col-name">名称</th>
            <th class="kng-col-type">类型</th>
            <th class="kng-col-user">上传人</th>
            <th class="kng-col-time">上传时间</th>
            <th class="kng-col-user">修改人</th>
            <th class="kng-col-time">修改时间</th>
            <th class="kng-col-status">状态</th>
            <th class="kng-col-actions">操作</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="sub in subdirs"
            :key="getSubdirKey(sub)"
            class="kng-table-row kng-table-row--dir"
            :class="{ 'drop-target': dirEditable }"
            v-droppable="dirEditable ? handleDropInSubdir : null"
            :data-dir-id="sub.id"
            @click.stop="$emit('on-dir-select', sub)"
            @contextmenu.prevent="showItemContextMenu($event, null, sub)"
          >
            <td class="kng-col-check"></td>
            <td class="kng-col-name">
              <div class="kng-table-name-cell">
                <span class="kng-table-icon kng-table-icon--dir">
                  <BaseIcon :icon="getSubdirIconMeta(sub).icon" :size="18" :color="getSubdirIconMeta(sub).color" />
                  <BaseIcon v-if="getSubdirIconMeta(sub).refBadge" icon="lianjie" :size="10" class="kng-table-ref-badge" />
                </span>
                <span class="kng-table-title" :title="sub.name">{{ sub.name }}</span>
              </div>
            </td>
            <td class="kng-col-type"><span class="kng-dir-type-tag">目录</span></td>
            <td class="kng-col-user">—</td>
            <td class="kng-col-time">—</td>
            <td class="kng-col-user">—</td>
            <td class="kng-col-time">—</td>
            <td class="kng-col-status">—</td>
            <td class="kng-col-actions"></td>
          </tr>
          <tr
            v-for="item in items"
            :key="item.id"
            class="kng-table-row"
            :class="{ 'is-selected': isSelected(item.id) }"
            v-draggable="dirEditable ? item.id : null"
            v-droppable="handleDropInFolder"
            :data-item-id="item.id"
            @click.stop="handleItemClick(item)"
            @dblclick.stop="handleItemDoubleClick(item)"
            @contextmenu.prevent="showItemContextMenu($event, item, null)"
          >
            <td class="kng-col-check" @click.stop>
              <Checkbox
                :value="isSelected(item.id)"
                @click.stop.native="handleCheckboxClick(item)"
              />
            </td>
            <td class="kng-col-name">
              <div class="kng-table-name-cell">
                <span class="kng-table-icon">
                  <BaseIcon :icon="getItemIcon(item)" :size="18" :color="getIconColor(item)" />
                  <BaseIcon v-if="item.ref" icon="lianjie" :size="10" class="kng-table-ref-badge" />
                </span>
                <span class="kng-table-title" :title="getDisplayTitle(item)">{{ getDisplayTitle(item) }}</span>
              </div>
            </td>
            <td class="kng-col-type">
              <span class="kng-type-badge kng-type-badge--inline" :class="getTypeBadgeClass(item)">{{ getTypeLabel(item) }}</span>
            </td>
            <td class="kng-col-user">
              <BaseNameBoard v-if="item.created_by || item.uploader" :uid="item.created_by || item.uploader" :size="16" hide-info />
              <span v-else class="kng-table-empty">—</span>
            </td>
            <td class="kng-col-time">{{ formatDate(item.created_at) || '—' }}</td>
            <td class="kng-col-user">
              <BaseNameBoard v-if="item.updated_by || item.modifier" :uid="item.updated_by || item.modifier" :size="16" hide-info />
              <span v-else class="kng-table-empty">—</span>
            </td>
            <td class="kng-col-time">{{ formatDate(item.updated_at) || '—' }}</td>
            <td class="kng-col-status">
              <div class="kng-table-status">
                <span v-if="isAuditPending(item)" class="meta-badge status-pending">未审核</span>
                <span v-if="getRagLabel(item)" class="meta-badge" :class="getRagClass(item)">{{ getRagLabel(item) }}</span>
                <span v-if="showMarkdownBtn(item)" class="meta-badge md-parse" :class="{ done: item.has_markdown || item.markdown_content }">已解析</span>
                <span v-if="!isAuditPending(item) && !getRagLabel(item) && !showMarkdownBtn(item)" class="kng-table-empty">—</span>
              </div>
            </td>
            <td class="kng-col-actions" @click.stop>
              <div class="kng-table-actions">
                <Button
                  v-if="showMarkdownBtn(item)"
                  type="text"
                  size="small"
                  :class="item.has_markdown || item.markdown_content ? 'md-btn-done' : 'md-btn-missing'"
                  @click.stop="$emit('on-markdown', item)"
                >MD</Button>
                <Button
                  type="text"
                  size="small"
                  icon="md-eye"
                  @click.stop="handleOpen(item)"
                >打开</Button>
                <Button
                  v-if="selectedItems.length === 1 && isSelected(item.id)"
                  type="text"
                  icon="md-create"
                  size="small"
                  @click.stop="handleEdit(item)"
                >编辑</Button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 摘要视图 -->
    <div v-else-if="viewMode === 'brief'" class="brief-view">
      <div
        v-for="sub in subdirs"
        :key="getSubdirKey(sub)"
        class="brief-item brief-item-dir"
        :class="{ 'drop-target': dirEditable }"
        v-droppable="dirEditable ? handleDropInSubdir : null"
        :data-dir-id="sub.id"
        @click.stop="$emit('on-dir-select', sub)"
        @contextmenu.prevent="showItemContextMenu($event, null, sub)"
      >
        <div class="brief-item-header">
          <div class="brief-item-icon">
            <BaseIcon :icon="getSubdirIconMeta(sub).icon" :size="24" :color="getSubdirIconMeta(sub).color" />
            <BaseIcon v-if="getSubdirIconMeta(sub).refBadge" icon="lianjie" :size="12" class="ref-badge-icon" />
          </div>
          <div class="brief-item-title">{{ sub.name }}</div>
        </div>
        <div class="brief-item-content">子目录</div>
      </div>
      <div
        v-for="item in items"
        :key="item.id"
        class="brief-item"
        :class="{ 'selected': isSelected(item.id) }"
        v-draggable="dirEditable ? item.id : null"
        v-droppable="handleDropInFolder"
        :data-item-id="item.id"
        @click.stop="handleItemClick(item)"
        @contextmenu.prevent="showItemContextMenu($event, item, null)"
      >
        <div class="brief-item-header">
          <div class="brief-item-icon">
            <BaseIcon :icon="getItemIcon(item)" :size="24" :color="getIconColor(item)" />
            <BaseIcon v-if="item.ref" icon="lianjie" :size="14" class="ref-badge-icon" />
          </div>
          <div class="brief-item-title-wrap">
            <div class="brief-item-title-row">
              <span class="kng-type-badge kng-type-badge--inline" :class="getTypeBadgeClass(item)">{{ getTypeLabel(item) }}</span>
              <span class="brief-item-title" :title="getDisplayTitle(item)">{{ getDisplayTitle(item) }}</span>
            </div>
          </div>
          <div class="brief-item-meta">
            <span class="meta-item">
              <span class="meta-label">上传:</span>
              <BaseNameBoard v-if="item.created_by || item.uploader" :uid="item.created_by || item.uploader" :size="16" hide-info />
              <span v-else>-</span>
              <span style="margin-left:5px;">{{ formatDate(item.created_at) }}</span>
            </span>
            <span class="meta-item" v-if="item.updated_by || item.modifier">
              <span class="meta-label">修改:</span>
              <BaseNameBoard v-if="item.updated_by || item.modifier" :uid="item.updated_by || item.modifier" :size="16" hide-info />
              <span v-else>-</span>
              <span style="margin-left:5px;">{{ formatDate(item.updated_at) }}</span>
            </span>
            <span v-if="item.good > 0" class="meta-badge good">
              <BaseIcon icon="md-thumbs-up" :size="12" style="margin-right:2px;" />
              {{ item.good }}
            </span>
            <span v-if="item.bad > 0" class="meta-badge bad">
              <BaseIcon icon="md-thumbs-down" :size="12" style="margin-right:2px;" />
              {{ item.bad }}
            </span>
            <span v-if="item.comment_count > 0" class="meta-badge">
              <BaseIcon icon="md-chatbubbles" :size="12" style="margin-right:2px;" />
              {{ item.comment_count }}
            </span>
          </div>
        </div>
        <div class="brief-item-content" v-html="getItemPreview(item)"></div>
        <div class="brief-item-footer">
          <Button type="primary" size="small" @click.stop="handleOpen(item)">打开</Button>
        </div>
      </div>
    </div>

    <!-- 选择框 -->
    <div
      v-if="selecting"
      class="selection-box"
      :style="selectionBoxStyle"
      @mousedown.stop
    ></div>

  </div>
</template>

<script>
import BaseEmpty from '@/components/base/Empty';
import BaseIcon from '@/components/base/Icon';
import BaseNameBoard from '@/components/base/NameBoard';
import emptyContentImg from '@/assets/svg/empty-content.svg';
import { getDirectoryIconMeta, getDirectoryVueKey } from '@/pages/core/knowledge/knowledge-permissions';
import {
  getFileExtFromName,
  isMdGenerateFileName,
} from '@/utils/mineru-parse';
import moment from 'moment';

export default {
  components: {BaseEmpty, BaseIcon, BaseNameBoard},
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    subdirs: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    viewMode: {
      type: String,
      default: 'icon',
    },
    allItems: {
      type: Array,
      default: () => [],
    },
    dirEditable: {
      type: Boolean,
      default: true,
    },
    canUpload: {
      type: Boolean,
      default: true,
    },
    uploadAccept: {
      type: String,
      default: '',
    },
    emptyUploadLabel: {
      type: String,
      default: '上传文件',
    },
    dirSelected: {
      type: Boolean,
      default: true,
    },
    allDirectories: {
      type: Array,
      default: () => [],
    },
    refDisplayContext: {
      type: Boolean,
      default: false,
    },
    refListParentId: {
      default: null,
    },
  },
  data() {
    return {
      emptyContentImg,
      selectedItems: [],
      selecting: false,
      selectionStart: null,
      selectionBoxStyle: {},
    };
  },
  computed: {
    hasNoContent() {
      const hasItems = this.items && this.items.length > 0;
      const hasSubdirs = this.subdirs && this.subdirs.length > 0;
      return !hasItems && !hasSubdirs;
    },
  },
  mounted() {
    this.$el.addEventListener('mousedown', this.handleMouseDown);
    document.addEventListener('mousemove', this.handleMouseMove);
    document.addEventListener('mouseup', this.handleMouseUp);
  },
  beforeDestroy() {
    this.$el.removeEventListener('mousedown', this.handleMouseDown);
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);
  },
  methods: {
    getItemIcon(item) {
      // 如果是文件类型，根据文件扩展名返回不同图标
      if (item.type === 'file' && item.content) {
        const url = item.content;
        const fileName = url.split('/').pop() || '';
        const ext = fileName.split('.').pop().toLowerCase();
        return this.getFileIconByExt(ext);
      }
      
      // 其他类型使用固定图标
      const iconMap = {
        text: 'md-document',
        md: 'logo-markdown',
        html: 'css',
        ref: 'lianjie',
      };
      return iconMap[item.type] || 'wenjian';
    },
    getFileIconByExt(ext) {
      const extIconMap = {
        // 文档类型
        'txt': 'md-document-text',
        'doc': 'md-document',
        'docx': 'md-document',
        'pdf': 'md-document',
        'xls': 'md-document',
        'xlsx': 'md-document',
        'ppt': 'md-document',
        'pptx': 'md-document',
        // 代码类型
        'js': 'codelibrary-fill',
        'ts': 'codelibrary-fill',
        'jsx': 'codelibrary-fill',
        'tsx': 'codelibrary-fill',
        'vue': 'codelibrary-fill',
        'py': 'codelibrary-fill',
        'java': 'codelibrary-fill',
        'cpp': 'codelibrary-fill',
        'c': 'codelibrary-fill',
        'go': 'codelibrary-fill',
        'php': 'codelibrary-fill',
        'rb': 'codelibrary-fill',
        // 标记语言
        'md': 'logo-markdown',
        'markdown': 'logo-markdown',
        'html': 'css',
        'htm': 'css',
        'xml': 'css',
        'json': 'codelibrary-fill',
        'yaml': 'codelibrary-fill',
        'yml': 'codelibrary-fill',
        // 图片类型
        'jpg': 'images',
        'jpeg': 'images',
        'png': 'images',
        'gif': 'images',
        'svg': 'images',
        'webp': 'images',
        'bmp': 'images',
        // 压缩文件
        'zip': 'wenjian',
        'rar': 'wenjian',
        '7z': 'wenjian',
        'tar': 'wenjian',
        'gz': 'wenjian',
        // 视频音频
        'mp4': 'shipin',
        'avi': 'shipin',
        'mov': 'shipin',
        'mp3': 'shipin',
        'wav': 'shipin',
      };
      return extIconMap[ext] || 'wenjian';
    },
    getIconColor(item) {
      // 根据类型返回不同的颜色
      const colorMap = {
        file: 'var(--primary)',
        text: '#1890ff',
        md: '#52c41a',
        html: '#fa8c16',
        ref: '#722ed1',
      };
      
      // 如果是文件类型，根据扩展名返回颜色
      if (item.type === 'file' && item.content) {
        const url = item.content;
        const fileName = url.split('/').pop() || '';
        const ext = fileName.split('.').pop().toLowerCase();
        
        // 代码文件
        if (['js', 'ts', 'jsx', 'tsx', 'vue', 'py', 'java', 'cpp', 'c', 'go', 'php', 'rb'].includes(ext)) {
          return '#52c41a';
        }
        // 标记语言
        if (['md', 'html', 'htm', 'xml', 'json', 'yaml', 'yml'].includes(ext)) {
          return ext === 'html' || ext === 'htm' ? '#fa8c16' : '#52c41a';
        }
        // 图片
        if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'bmp'].includes(ext)) {
          return '#eb2f96';
        }
        // 视频音频
        if (['mp4', 'avi', 'mov', 'mp3', 'wav'].includes(ext)) {
          return '#13c2c2';
        }
      }
      
      return colorMap[item.type] || 'var(--primary)';
    },
    resolveSourceItem(item) {
      if (item.ref && item.content) {
        return this.allItems.find(i => i.id === item.content) || item;
      }
      return item;
    },
    getTypeLabel(item) {
      const source = this.resolveSourceItem(item);
      const map = { md: 'MD', text: 'TXT', file: 'FILE', html: 'HTML' };
      return map[source.type] || 'FILE';
    },
    getTypeBadgeClass(item) {
      const label = this.getTypeLabel(item);
      return {
        MD: 'type-md',
        TXT: 'type-txt',
        FILE: 'type-file',
        HTML: 'type-html',
      }[label] || 'type-file';
    },
    getFileName(item) {
      const source = this.resolveSourceItem(item);
      if (!source.content) return '未命名文件';
      const url = source.content;
      let fileName = decodeURIComponent((url.split('/').pop() || '').split('?')[0]) || '未命名文件';
      const ext = fileName.includes('.') ? fileName.split('.').pop().toLowerCase() : '';
      if (source.name && ext) {
        fileName = `${source.name}.${ext}`;
      }
      return fileName;
    },
    getDisplayTitle(item) {
      const source = this.resolveSourceItem(item);
      if (source.type === 'file') {
        return this.getFileName(source);
      }
      if (source.name) return source.name;
      return this.getItemName(source);
    },
    getItemName(item) {
      if (item.type === 'text' || item.type === 'md' || item.type === 'html') {
        const content = item.content || '';
        const firstLine = content.split('\n')[0] || '未命名文档';
        return firstLine.substring(0, 30) + (firstLine.length > 30 ? '...' : '');
      }
      return '未命名';
    },
    getItemPreview(item) {
      const source = this.resolveSourceItem(item);
      if (!source.content) return '暂无内容';
      if (source.type === 'file') {
        return `<span class="brief-file-link">${this.getFileName(source)}</span>`;
      }
      if (source.type === 'text' || source.type === 'md') {
        const content = source.content.substring(0, 200);
        return content + (source.content.length > 200 ? '...' : '');
      }
      if (source.type === 'html') {
        const div = document.createElement('div');
        div.innerHTML = source.content;
        return div.textContent || div.innerText || 'HTML内容';
      }
      return '暂无预览';
    },
    getSubdirKey(sub) {
      return getDirectoryVueKey(sub, {
        refList: this.refDisplayContext,
        parentId: this.refDisplayContext ? this.refListParentId : null,
      });
    },
    getSubdirIconMeta(sub) {
      const meta = getDirectoryIconMeta(sub, { allDirectories: this.allDirectories });
      if (this.refDisplayContext) {
        return { ...meta, refBadge: true };
      }
      return meta;
    },
    isDirApprovalRequired(item) {
      if (!item || item.dir_id == null) return false;
      const dir = (this.allDirectories || []).find(d => String(d.id) === String(item.dir_id));
      return !!(dir && dir.enable_approval);
    },
    isAuditPending(item) {
      if (!item || item.type === 'ref') return false;
      if (!this.isDirApprovalRequired(item)) return false;
      const s = item.audit_status;
      if (s === 'approved') return false;
      return s === 'pending' || s === 'unreviewed' || s == null || s === 0;
    },
    getRagLabel(item) {
      if (!item || item.rag_status == null || item.rag_status === '') return '';
      const done = item.rag_status === 'done' || item.rag_status === 'completed' || item.rag_status === true;
      return done ? 'RAG已上传' : 'RAG待处理';
    },
    getRagClass(item) {
      const done = item.rag_status === 'done' || item.rag_status === 'completed' || item.rag_status === true;
      return done ? 'rag-done' : 'rag-pending';
    },
    showMarkdownBtn(item) {
      if (!item || item.ref || item.type === 'md') return false;
      return item.type === 'file';
    },
    formatDate(date) {
      if (!date) return '';
      return moment(date).format('YYYY-MM-DD HH:mm');
    },
    isSelected(id) {
      return this.selectedItems.some(item => item.id === id);
    },
    handleItemClick(item) {
      if (this.viewMode === 'icon') {
        // 图标模式下，点击切换选择
        const index = this.selectedItems.findIndex(i => i.id === item.id);
        if (index >= 0) {
          this.selectedItems.splice(index, 1);
        } else {
          this.selectedItems = [item];
        }
        this.$emit('on-select', this.selectedItems);
      } else {
        // 其他模式下，点击选择
        if (!this.isSelected(item.id)) {
          this.selectedItems = [item];
          this.$emit('on-select', this.selectedItems);
        }
      }
    },
    handleItemDoubleClick(item) {
      if (this.viewMode === 'icon' || this.viewMode === 'list') {
        this.$emit('on-open', item);
      }
    },
    handleCheckboxClick(item) {
      const index = this.selectedItems.findIndex(i => i.id === item.id);
      if (index >= 0) {
        this.selectedItems.splice(index, 1);
      } else {
        this.selectedItems.push(item);
      }
      this.$emit('on-select', this.selectedItems);
    },
    handleEdit(item) {
      this.$emit('on-edit', item);
    },
    handleOpen(item) {
      this.$emit('on-open', item);
    },
    handleContainerClick(e) {
      if (e.target === this.$el || e.target.classList.contains('icon-view')) {
        this.selectedItems = [];
        this.$emit('on-select', []);
      }
    },
    handleDropInFolder(draggedId, targetData) {
      if (!targetData || !targetData.itemId) return;
      this.$emit('on-drop-in-folder', {
        draggedId,
        targetItemId: targetData.itemId,
      });
    },
    handleDropInSubdir(draggedId, targetData) {
      if (!targetData || !targetData.dirId) return;
      this.$emit('on-drop-in-dir', {
        draggedId,
        targetDirId: targetData.dirId,
      });
    },
    handleMouseDown(e) {
      if (this.viewMode !== 'icon') return;
      if (e.target.closest('.icon-item')) return;
      
      this.selecting = true;
      const rect = this.$el.getBoundingClientRect();
      this.selectionStart = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
      this.selectionBoxStyle = {
        left: this.selectionStart.x + 'px',
        top: this.selectionStart.y + 'px',
        width: '0px',
        height: '0px',
        display: 'block',
      };
    },
    handleMouseMove(e) {
      if (!this.selecting) return;
      const rect = this.$el.getBoundingClientRect();
      const currentX = e.clientX - rect.left;
      const currentY = e.clientY - rect.top;
      
      const left = Math.min(this.selectionStart.x, currentX);
      const top = Math.min(this.selectionStart.y, currentY);
      const width = Math.abs(currentX - this.selectionStart.x);
      const height = Math.abs(currentY - this.selectionStart.y);
      
      this.selectionBoxStyle = {
        left: left + 'px',
        top: top + 'px',
        width: width + 'px',
        height: height + 'px',
        display: 'block',
      };

      // 选择框内的项目
      const selected = [];
      this.items.forEach(item => {
        const itemEl = this.$el.querySelector(`[data-item-id="${item.id}"]`);
        if (itemEl) {
          const itemRect = itemEl.getBoundingClientRect();
          const itemLeft = itemRect.left - rect.left;
          const itemTop = itemRect.top - rect.top;
          const itemRight = itemLeft + itemRect.width;
          const itemBottom = itemTop + itemRect.height;
          
          if (itemLeft < left + width && itemRight > left &&
              itemTop < top + height && itemBottom > top) {
            selected.push(item);
          }
        }
      });
      this.selectedItems = selected;
      this.$emit('on-select', selected);
    },
    handleMouseUp() {
      if (this.selecting) {
        this.selecting = false;
        this.selectionBoxStyle = { display: 'none' };
      }
    },
    handleFileDrop(e) {
      e.preventDefault();
      e.stopPropagation();
      const files = Array.from(e.dataTransfer.files || []);
      if (files.length > 0) {
        this.$emit('on-file-drop', files);
      }
    },
    handleEmptyUpload(e) {
      const files = Array.from(e.target.files || []);
      if (files.length > 0) {
        this.$emit('on-file-drop', files);
      }
      e.target.value = '';
    },
    showItemContextMenu(e, item, subdir) {
      const menuItem = item || null;
      const menuSubdir = subdir || null;
      const canEdit = !!this.dirEditable;
      if (menuSubdir) {
        const items = [
          { label: '单独打开', onClick: () => {
            this.$emit('on-context-menu', { action: 'open-alone', dir: menuSubdir });
          }},
          { label: '作为文档打开', onClick: () => {
            this.$emit('on-context-menu', { action: 'open-as-doc', dir: menuSubdir });
          }},
        ];
        if (canEdit) {
          items.push(
            { label: '打开', onClick: () => {
              this.$emit('on-dir-select', menuSubdir);
            }},
          );
        }
        this.$contextmenu({
          event: e,
          items,
          zIndex: 9999,
          minWidth: 120,
        });
        return;
      }
      const items = [
        { label: '打开', onClick: () => {
          if (menuItem) this.$emit('on-open', menuItem);
        }},
      ];
      if (menuItem && this.canDownloadItem(menuItem)) {
        items.push({
          label: '下载',
          onClick: () => {
            this.$emit('on-context-menu', { action: 'download', item: menuItem });
          },
        });
      }
      if (canEdit) {
        items.push(
          { label: '编辑', onClick: () => {
            if (menuItem) this.$emit('on-context-menu', { action: 'edit', item: menuItem });
          }},
          { label: '删除', onClick: () => {
            if (menuItem) this.$emit('on-context-menu', { action: 'remove', item: menuItem });
          }},
        );
        if (menuItem && this.canGenerateMd(menuItem)) {
          items.push({
            label: '生成 MD 文件',
            onClick: () => {
              this.$emit('on-context-menu', { action: 'generate-md', item: menuItem });
            },
          });
        }
      }
      this.$contextmenu({
        event: e,
        items,
        zIndex: 9999,
        minWidth: 120,
      });
    },
    canDownloadItem(item) {
      const source = this.resolveSourceItem(item);
      if (!source) return false;
      if (source.type === 'file' && source.content) return true;
      if (source.type === 'md') {
        const text = (source.content || source.markdown_content || '').trim();
        return !!text;
      }
      return false;
    },
    canGenerateMd(item) {
      const source = this.resolveSourceItem(item);
      if (!source || source.ref || source.type !== 'file' || !source.content) return false;
      const fromUrl = isMdGenerateFileName(source.content);
      if (fromUrl) return true;
      if (source.name) {
        const ext = getFileExtFromName(source.name);
        if (['pdf', 'doc', 'docx'].includes(ext)) return true;
        // name 无后缀时，再看 URL
        return isMdGenerateFileName(source.content);
      }
      return false;
    },
  },
};
</script>

<style lang="less" scoped>
.knowledge-list {
  background:
    radial-gradient(ellipse 80% 50% at 50% -10%, rgba(51, 153, 255, 0.06), transparent 55%),
    var(--bg2);
}

.kng-type-badge {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 34px;
  height: 20px;
  padding: 0 7px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  line-height: 1;
  border: 1px solid transparent;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);

  &--inline {
    position: static;
    flex-shrink: 0;
    min-width: 40px;
    height: 20px;
    font-size: 11px;
  }

  &.type-md {
    color: #237804;
    background: #f6ffed;
    border-color: #b7eb8f;
  }

  &.type-txt {
    color: #0958d9;
    background: #e6f4ff;
    border-color: #91caff;
  }

  &.type-file {
    color: #ad4e00;
    background: #fff7e6;
    border-color: #ffd591;
  }

  &.type-html {
    color: #531dab;
    background: #f9f0ff;
    border-color: #d3adf7;
  }
}

.icon-view {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 16px;
  padding: 18px 20px 24px;

  .icon-item {
    min-height: 148px;
    border: 1px solid rgba(0, 0, 0, 0.06);
    border-radius: 14px;
    cursor: pointer;
    transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease, background 0.18s ease;
    display: flex;
    flex-direction: column;
    position: relative;
    padding: 16px 12px 12px;
    background: rgba(255, 255, 255, 0.92);
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);

    &:hover {
      border-color: rgba(51, 153, 255, 0.35);
      box-shadow: 0 8px 22px rgba(15, 23, 42, 0.08);
      transform: translateY(-2px);
    }

    &.selected {
      background: linear-gradient(180deg, rgba(51, 153, 255, 0.1) 0%, rgba(255, 255, 255, 0.96) 100%);
      border-color: rgba(51, 153, 255, 0.55);
      box-shadow: 0 8px 22px rgba(51, 153, 255, 0.14);
    }

    &.icon-item-dir {
      background: linear-gradient(180deg, #fffaf0 0%, #fff 72%);

      &:hover {
        border-color: rgba(250, 173, 20, 0.45);
        box-shadow: 0 8px 22px rgba(250, 173, 20, 0.12);
      }
    }

    .icon-item-content {
      text-align: center;
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
      padding-top: 4px;

      .icon-item-icon-wrapper {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 64px;
        height: 64px;
        border-radius: 16px;
        background: linear-gradient(180deg, var(--bg3) 0%, rgba(255, 255, 255, 0.8) 100%);
        border: 1px solid rgba(0, 0, 0, 0.04);
        margin: 0 auto 12px;
        position: relative;
        flex-shrink: 0;

        .ref-badge-icon {
          position: absolute;
          bottom: -2px;
          right: -2px;
          background: var(--primary);
          color: #fff;
          border-radius: 50%;
          padding: 2px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
        }
      }

      .icon-item-name {
        width: 100%;
        font-size: 13px;
        font-weight: 560;
        line-height: 1.45;
        color: var(--text);
        word-break: break-all;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
        min-height: 2.9em;
        padding: 0 2px;
      }

      .icon-item-status {
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
        justify-content: center;
        margin-top: 8px;
        min-height: 18px;

        .icon-status-tag {
          font-size: 10px;
          padding: 2px 7px;
          border-radius: 999px;
          background: var(--bg2);
          border: 1px solid var(--border);
          color: var(--subtext3);

          &.pending {
            color: #d48806;
            border-color: rgba(250, 173, 20, 0.35);
            background: rgba(250, 173, 20, 0.08);
          }

          &.rag-done {
            color: #389e0d;
            background: rgba(82, 196, 26, 0.08);
          }

          &.rag-pending {
            color: #722ed1;
            background: rgba(114, 46, 209, 0.08);
          }

          &.md-parse.done {
            color: #389e0d;
            border-color: rgba(82, 196, 26, 0.35);
            background: rgba(82, 196, 26, 0.08);
          }
        }
      }
    }
  }
}

.list-view {
  padding: 12px 14px 18px;
  background: transparent;
}

.kng-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
  font-size: 13px;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);

  thead {
    position: sticky;
    top: 0;
    z-index: 2;

    th {
      padding: 11px 14px;
      text-align: left;
      font-size: 12px;
      font-weight: 600;
      letter-spacing: 0.02em;
      color: var(--subtext2);
      background: linear-gradient(180deg, #f8fafc 0%, #f3f6f9 100%);
      border-bottom: 1px solid rgba(0, 0, 0, 0.06);
      white-space: nowrap;
    }
  }

  tbody {
    .kng-table-row {
      cursor: pointer;
      transition: background 0.15s;

      &:hover {
        background: rgba(51, 153, 255, 0.045);
      }

      &.is-selected {
        background: rgba(51, 153, 255, 0.08);

        td:first-child {
          box-shadow: inset 3px 0 0 var(--primary);
        }
      }

      &.kng-table-row--dir {
        background: linear-gradient(90deg, rgba(250, 173, 20, 0.07) 0%, transparent 28%);

        &:hover {
          background: linear-gradient(90deg, rgba(250, 173, 20, 0.12) 0%, rgba(51, 153, 255, 0.04) 28%);
        }
      }

      td {
        padding: 12px 14px;
        border-bottom: 1px solid rgba(0, 0, 0, 0.045);
        color: var(--text);
        vertical-align: middle;
      }

      &:last-child td {
        border-bottom: none;
      }
    }
  }
}

.kng-col-check {
  width: 44px;
  text-align: center;
}

.kng-col-name {
  width: 28%;
  min-width: 180px;
}

.kng-col-type {
  width: 72px;
}

.kng-col-user {
  width: 110px;
}

.kng-col-time {
  width: 132px;
  color: var(--subtext2);
  font-size: 12px;
  white-space: nowrap;
}

.kng-col-status {
  width: 140px;
}

.kng-col-actions {
  width: 148px;
  text-align: right;
}

.kng-table-name-cell {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.kng-table-icon {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 9px;
  background: var(--bg3);
  border: 1px solid rgba(0, 0, 0, 0.04);
  flex-shrink: 0;

  &--dir {
    background: rgba(250, 173, 20, 0.12);
    border-color: rgba(250, 173, 20, 0.18);
  }
}

.kng-table-ref-badge {
  position: absolute;
  right: -3px;
  bottom: -3px;
  background: var(--primary);
  color: #fff;
  border-radius: 50%;
  padding: 1px;
}

.kng-table-title {
  font-weight: 600;
  color: var(--text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.kng-dir-type-tag {
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  color: #ad6800;
  background: #fff7e6;
  border: 1px solid #ffd591;
}

.kng-table-empty {
  color: var(--subtext3);
}

.kng-table-status {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;

  .meta-badge {
    padding: 2px 7px;
    background: var(--bg3);
    border-radius: 10px;
    font-size: 11px;
    display: inline-flex;
    align-items: center;
    border: 1px solid var(--border);
    color: var(--subtext3);
    white-space: nowrap;

    &.status-pending {
      color: #d48806;
      border-color: rgba(250, 173, 20, 0.35);
      background: rgba(250, 173, 20, 0.08);
    }

    &.rag-done {
      color: #389e0d;
      background: rgba(82, 196, 26, 0.08);
    }

    &.rag-pending {
      color: #722ed1;
      background: rgba(114, 46, 209, 0.08);
    }

    &.md-parse.done {
      color: #389e0d;
      border-color: rgba(82, 196, 26, 0.35);
      background: rgba(82, 196, 26, 0.08);
    }
  }
}

.kng-table-actions {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 2px;
  flex-wrap: nowrap;

  .md-btn-done {
    color: #389e0d;
    font-weight: 600;
  }

  .md-btn-missing {
    color: var(--subtext3);
  }
}

.brief-view {
  padding: 16px 18px 22px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  .brief-item {
    border: 1px solid rgba(0, 0, 0, 0.06);
    border-radius: 14px;
    padding: 16px 18px;
    cursor: pointer;
    transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease, background 0.18s ease;
    background: rgba(255, 255, 255, 0.94);
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);

    &:hover {
      border-color: rgba(51, 153, 255, 0.35);
      box-shadow: 0 8px 22px rgba(15, 23, 42, 0.08);
      transform: translateY(-1px);
    }

    &.selected {
      border-color: rgba(51, 153, 255, 0.55);
      background: linear-gradient(180deg, rgba(51, 153, 255, 0.08) 0%, rgba(255, 255, 255, 0.96) 100%);
      box-shadow: 0 8px 22px rgba(51, 153, 255, 0.12);
    }

    &.brief-item-dir {
      background: linear-gradient(180deg, #fffaf0 0%, #fff 100%);
    }

    .brief-item-header {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      margin-bottom: 10px;

      .brief-item-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 42px;
        height: 42px;
        border-radius: 11px;
        background: linear-gradient(180deg, var(--bg3) 0%, rgba(255, 255, 255, 0.85) 100%);
        border: 1px solid rgba(0, 0, 0, 0.04);
        flex-shrink: 0;
        position: relative;

        .ref-badge-icon {
          position: absolute;
          bottom: -2px;
          right: -2px;
          background: var(--primary);
          color: #fff;
          border-radius: 50%;
          padding: 1px;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
        }
      }

      .brief-item-title-wrap {
        flex: 1;
        min-width: 0;
      }

      .brief-item-title-row {
        display: flex;
        align-items: center;
        gap: 8px;
        min-width: 0;
      }

      .brief-item-title {
        font-weight: 600;
        font-size: 15px;
        color: var(--text);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .brief-item-meta {
        display: flex;
        gap: 10px;
        align-items: center;
        font-size: 12px;
        color: var(--subtext2);
        flex-shrink: 0;
        flex-wrap: wrap;
        justify-content: flex-end;

        .meta-item {
          display: flex;
          align-items: center;
          gap: 4px;

          .meta-label {
            color: var(--subtext3);
          }
        }

        .meta-badge {
          padding: 2px 7px;
          border-radius: 10px;
          font-size: 11px;
          background: var(--bg3);
          border: 1px solid var(--border);
          color: var(--subtext3);
        }
      }
    }

    .brief-item-content {
      color: var(--subtext2);
      font-size: 13px;
      line-height: 1.65;
      margin-bottom: 10px;
      max-height: 84px;
      overflow: hidden;
      padding: 10px 12px;
      border-radius: 8px;
      background: var(--bg2);
      border: 1px solid var(--border);

      /deep/ .brief-file-link {
        color: var(--text);
        font-family: Consolas, Monaco, 'Courier New', monospace;
        font-size: 12px;
      }
    }

    .brief-item-footer {
      display: flex;
      justify-content: flex-end;
    }
  }
}

.knowledge-list--empty {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/deep/ .kng-empty-fill.l-empty-holder {
  flex: 1;
  width: 100%;
  height: 100%;
  min-height: 0;
  background: transparent;
  border-radius: 0;
  gap: 10px;
}

.kng-empty-img {
  width: 36%;
  max-width: 220px;
  max-height: 36%;
  opacity: 0.92;
}

.kng-empty-hint {
  color: var(--subtext3);
  font-size: 13px;
  line-height: 1.5;
}

.kng-empty-upload-btn {
  margin-top: 8px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 18px;
  border-radius: 999px;
  border: 1px dashed rgba(51, 153, 255, 0.55);
  color: var(--primary);
  font-size: 13px;
  font-weight: 560;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, box-shadow 0.15s;
  background: rgba(51, 153, 255, 0.04);

  &:hover {
    background: rgba(51, 153, 255, 0.1);
    border-color: var(--primary);
    box-shadow: 0 4px 12px rgba(51, 153, 255, 0.12);
  }
}

.selection-box {
  position: absolute;
  border: 2px dashed var(--primary);
  background: rgba(51, 153, 255, 0.1);
  pointer-events: none;
  z-index: 10;
  display: none;
}

.ugo-draggable {
  opacity: 0.5;
}

.ugo-droppable {
  background: var(--primary-light) !important;
  border: 2px dashed var(--primary) !important;
}

.icon-item-dir.drop-target,
.kng-table-row--dir.drop-target,
.brief-item-dir.drop-target {
  transition: box-shadow 0.2s, transform 0.2s;
}

.icon-item-dir.ugo-droppable,
.kng-table-row--dir.ugo-droppable,
.brief-item-dir.ugo-droppable {
  transform: scale(1.01);
  box-shadow: inset 0 0 0 2px var(--primary);
}

</style>
